import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { FETCH_TENANT_SSO_SETTINGS } from '@modules/workspaceSettings/hooks/useFetchTenantSSOSettings';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
} from '@mui/material';
import { useUpdateTenantSsoMutation } from 'generated/graphql';
import { useGetUser } from 'v2/core/services/user';

interface EnforceSSOModalContentProps {
  onClose: () => void;
}

export const EnforceSSOModalContent = ({
  onClose,
}: EnforceSSOModalContentProps) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const { closeModal } = useMuiModalContext();
  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const [updateTenantSSO, { loading }] = useUpdateTenantSsoMutation({
    variables: {
      request: {
        tenantID,
        isEnforced: true,
      },
    },
    refetchQueries: [FETCH_TENANT_SSO_SETTINGS],
  });

  const onCancel = () => {
    onClose();
    closeModal();
  };

  const onProceed = async () => {
    try {
      await updateTenantSSO();
      closeModal();
      showSuccessNotification({
        contentComponent: 'SSO Enforced for organization',
        props: { isClosable: true },
      });
    } catch {
      showErrorNotification({
        contentComponent: 'Failed to enforce SSO',
        props: { isClosable: true },
      });
    }
  };

  return (
    <>
      <ModalHeader title="Enforce SSO" onClose={onClose} />
      <Divider />
      <DialogContent>
        <Typography variant="body2">
          Enforcing SSO might require you and your teammates to re-authenticate.
          Please confirm that SSO is correctly configured before proceeding. You
          can test your SSO configuration by signing-in via SSO.
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ m: 1, mr: 2 }}>
        <Button variant="outlined" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          autoFocus
          onClick={onProceed}
          loading={loading}
        >
          Proceed
        </LoadingButton>
      </DialogActions>
    </>
  );
};
